import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Users, DollarSign, TrendingUp, MousePointer, Eye, BarChart3,
  ArrowLeft, CheckCircle, Clock, XCircle, AlertTriangle,
  CreditCard, ChevronLeft, ChevronRight, Repeat, Shield, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminAffiliates() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [fraudFlags, setFraudFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState({});

  const headers = useCallback(() => {
    const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  }, []);

  const loadTab = useCallback(async (t, page = 1) => {
    setLoading(true);
    try {
      if (t === 'overview') {
        const res = await fetch(`${API}/affiliate/admin/overview`, { headers: headers() });
        if (res.ok) setOverview(await res.json());
      } else if (t === 'affiliates') {
        const res = await fetch(`${API}/affiliate/admin/affiliates?page=${page}`, { headers: headers() });
        if (res.ok) { const d = await res.json(); setAffiliates(d.affiliates || []); setPages(p => ({ ...p, affiliates: { current: d.page, total: Math.ceil(d.total / 20) } })); }
      } else if (t === 'referrals') {
        const res = await fetch(`${API}/affiliate/admin/referrals?page=${page}`, { headers: headers() });
        if (res.ok) { const d = await res.json(); setReferrals(d.referrals || []); setPages(p => ({ ...p, referrals: { current: d.page, total: Math.ceil(d.total / 20) } })); }
      } else if (t === 'conversions') {
        const res = await fetch(`${API}/affiliate/admin/conversions?page=${page}`, { headers: headers() });
        if (res.ok) { const d = await res.json(); setConversions(d.conversions || []); setPages(p => ({ ...p, conversions: { current: d.page, total: Math.ceil(d.total / 20) } })); }
      } else if (t === 'payouts') {
        const res = await fetch(`${API}/affiliate/admin/payouts?page=${page}`, { headers: headers() });
        if (res.ok) { const d = await res.json(); setPayouts(d.payouts || []); setPages(p => ({ ...p, payouts: { current: d.page, total: Math.ceil(d.total / 20) } })); }
      } else if (t === 'fraud') {
        const res = await fetch(`${API}/affiliate/admin/fraud-flags?page=${page}`, { headers: headers() });
        if (res.ok) { const d = await res.json(); setFraudFlags(d.flags || []); setPages(p => ({ ...p, fraud: { current: d.page, total: Math.ceil(d.total / 20) } })); }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { loadTab('overview'); }, [loadTab]);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const updateConversion = async (id, status) => {
    const res = await fetch(`${API}/affiliate/admin/conversions/${id}`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ status }),
    });
    if (res.ok) { toast.success(`Conversion ${status}`); loadTab('conversions'); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'affiliates', label: 'Affiliates', icon: Users },
    { id: 'referrals', label: 'Referrals', icon: TrendingUp },
    { id: 'conversions', label: 'Conversions', icon: DollarSign },
    { id: 'payouts', label: 'Payouts', icon: CreditCard },
    { id: 'fraud', label: 'Fraud Flags', icon: AlertTriangle },
  ];

  const statusBadge = (status) => {
    const map = {
      active: 'bg-green-50 text-green-700', pending: 'bg-amber-50 text-amber-700', approved: 'bg-blue-50 text-blue-700',
      paid: 'bg-green-50 text-green-700', reversed: 'bg-red-50 text-red-700', rejected: 'bg-red-50 text-red-700',
      held: 'bg-orange-50 text-orange-700', converted: 'bg-green-50 text-green-700', qualified: 'bg-blue-50 text-blue-700',
      open: 'bg-red-50 text-red-700', cleared: 'bg-gray-50 text-gray-600', paused: 'bg-gray-50 text-gray-600',
      expired: 'bg-gray-50 text-gray-400', void: 'bg-gray-50 text-gray-400',
    };
    return <Badge className={`text-xs capitalize ${map[status] || 'bg-gray-50 text-gray-600'}`}>{status}</Badge>;
  };

  const Paginator = ({ section }) => {
    const p = pages[section];
    if (!p || p.total <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-gray-100">
        <Button variant="ghost" size="sm" disabled={p.current <= 1} onClick={() => loadTab(tab, p.current - 1)}><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm text-gray-500">Page {p.current} of {p.total}</span>
        <Button variant="ghost" size="sm" disabled={p.current >= p.total} onClick={() => loadTab(tab, p.current + 1)}><ChevronRight className="w-4 h-4" /></Button>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-affiliates-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
          <div>
            <h1 className="text-xl font-bold text-[#0f1f33]">Affiliate Management</h1>
            <p className="text-sm text-gray-400">Manage partners, referrals, conversions, and payouts.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => {
          const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
          fetch(`${API}/affiliate/admin/export-csv`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.blob()).then(blob => {
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'area_affiliate_report.csv'; a.click(); URL.revokeObjectURL(url);
              toast.success('Report downloaded');
            }).catch(() => toast.error('Failed to download'));
        }} data-testid="export-admin-csv">
          <Download className="w-4 h-4 mr-2" /> Export Full Report
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-50 p-1 rounded-xl overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-white text-[#0f1f33] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`admin-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-50 rounded-xl animate-pulse border border-gray-100" />)}</div>}

      {/* Overview */}
      {!loading && tab === 'overview' && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Active Partners", value: overview.total_partners, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "Total Clicks", value: overview.total_clicks, icon: MousePointer, color: "text-amber-600", bg: "bg-amber-50" },
              { label: "Conversions", value: overview.total_conversions, icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-50" },
              { label: "Pending Approvals", value: overview.pending_conversions, icon: Clock, color: "text-orange-600", bg: "bg-orange-50" },
              { label: "Total Commissions", value: fmt(overview.total_commissions), icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Total Paid Out", value: fmt(overview.total_paid), icon: CreditCard, color: "text-indigo-600", bg: "bg-indigo-50" },
              { label: "Pending Liability", value: fmt(overview.pending_liability), icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
              { label: "Gross Revenue", value: fmt(overview.gross_revenue), icon: BarChart3, color: "text-green-600", bg: "bg-green-50" },
            ].map((m, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-7 h-7 rounded-lg ${m.bg} flex items-center justify-center`}><m.icon className={`w-3.5 h-3.5 ${m.color}`} /></div>
                  <span className="text-xs text-gray-400 font-medium">{m.label}</span>
                </div>
                <p className="text-lg font-bold text-[#0f1f33]">{m.value}</p>
              </div>
            ))}
          </div>
          {/* Top Partners */}
          {overview.top_partners?.length > 0 && (
            <Card className="border-gray-100 shadow-sm">
              <CardContent className="p-5">
                <h3 className="font-semibold text-[#0f1f33] mb-4">Top Partners</h3>
                <div className="space-y-3">
                  {overview.top_partners.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-gray-400 w-6">#{i + 1}</span>
                        <div>
                          <p className="text-sm font-medium text-[#0f1f33]">{p.name}</p>
                          <p className="text-xs text-gray-400 capitalize">{p.partner_type?.replace('_', ' ')} &middot; {p.tier}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-600">{fmt(p.total_earned)}</p>
                        <p className="text-xs text-gray-400">{p.total_conversions} conversions</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {/* Recent Conversions */}
          {overview.recent_conversions?.length > 0 && (
            <Card className="border-gray-100 shadow-sm">
              <CardContent className="p-5">
                <h3 className="font-semibold text-[#0f1f33] mb-4">Recent Conversions</h3>
                <div className="space-y-3">
                  {overview.recent_conversions.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-[#0f1f33]">{c.member_name}</p>
                        <p className="text-xs text-gray-400">via {c.partner_name} &middot; {c.event_type?.replace('_', ' ')}</p>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <p className="text-sm font-semibold text-emerald-600">{fmt(c.commission_amount)}</p>
                        {statusBadge(c.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Affiliates Tab */}
      {!loading && tab === 'affiliates' && (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Partner</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Type</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Code</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Clicks</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Conversions</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Earned</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Paid</th>
                  <th className="text-center py-3 px-2 text-xs text-gray-400 font-medium">Bank</th>
                  <th className="text-center py-3 px-2 text-xs text-gray-400 font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {affiliates.map((a, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-3 px-2">
                        <p className="font-medium text-[#0f1f33]">{a.name}</p>
                        <p className="text-xs text-gray-400">{a.email}</p>
                      </td>
                      <td className="py-3 px-2 capitalize text-gray-600">{a.partner_type?.replace('_', ' ')}</td>
                      <td className="py-3 px-2 font-mono text-xs text-gray-500">{a.referral_code}</td>
                      <td className="py-3 px-2 text-right">{a.total_clicks}</td>
                      <td className="py-3 px-2 text-right">{a.total_conversions}</td>
                      <td className="py-3 px-2 text-right font-medium text-emerald-600">{fmt(a.total_earned)}</td>
                      <td className="py-3 px-2 text-right text-gray-500">{fmt(a.total_paid)}</td>
                      <td className="py-3 px-2 text-center">{a.stripe_onboarding_complete ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                      <td className="py-3 px-2 text-center">{statusBadge(a.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {affiliates.length === 0 && <p className="text-gray-400 text-center py-8 text-sm">No affiliate profiles found.</p>}
            </div>
            <Paginator section="affiliates" />
          </CardContent>
        </Card>
      )}

      {/* Referrals Tab */}
      {!loading && tab === 'referrals' && (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Partner</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Referred Member</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Campaign</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Created</th>
                  <th className="text-center py-3 px-2 text-xs text-gray-400 font-medium">Locked</th>
                  <th className="text-center py-3 px-2 text-xs text-gray-400 font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {referrals.map((r, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-3 px-2 font-medium text-[#0f1f33]">{r.partner_name}</td>
                      <td className="py-3 px-2 text-gray-600">{r.referred_name}</td>
                      <td className="py-3 px-2 text-gray-500 text-xs">{r.campaign_name || '—'}</td>
                      <td className="py-3 px-2 text-gray-400 text-xs">{r.created_at?.slice(0, 10)}</td>
                      <td className="py-3 px-2 text-center">{r.attribution_locked ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <Clock className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                      <td className="py-3 px-2 text-center">{statusBadge(r.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {referrals.length === 0 && <p className="text-gray-400 text-center py-8 text-sm">No referrals found.</p>}
            </div>
            <Paginator section="referrals" />
          </CardContent>
        </Card>
      )}

      {/* Conversions Tab */}
      {!loading && tab === 'conversions' && (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Partner</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Member</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Event</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Gross</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Commission</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Rate</th>
                  <th className="text-center py-3 px-2 text-xs text-gray-400 font-medium">Status</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Actions</th>
                </tr></thead>
                <tbody>
                  {conversions.map((c, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-3 px-2 font-medium text-[#0f1f33]">{c.partner_name}</td>
                      <td className="py-3 px-2 text-gray-600">{c.member_name}</td>
                      <td className="py-3 px-2 capitalize text-gray-500 text-xs">{c.event_type?.replace('_', ' ')}</td>
                      <td className="py-3 px-2 text-right">{fmt(c.gross_amount)}</td>
                      <td className="py-3 px-2 text-right font-medium text-emerald-600">{fmt(c.commission_amount)}</td>
                      <td className="py-3 px-2 text-right text-gray-500">{c.commission_rate}%</td>
                      <td className="py-3 px-2 text-center">{statusBadge(c.status)}</td>
                      <td className="py-3 px-2 text-right">
                        {c.status === 'pending' && (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" className="text-green-600 h-7 text-xs" onClick={() => updateConversion(c.conversion_id, 'approved')}>Approve</Button>
                            <Button size="sm" variant="ghost" className="text-red-600 h-7 text-xs" onClick={() => updateConversion(c.conversion_id, 'rejected')}>Reject</Button>
                          </div>
                        )}
                        {c.status === 'approved' && (
                          <Button size="sm" variant="ghost" className="text-red-500 h-7 text-xs" onClick={() => updateConversion(c.conversion_id, 'reversed')}>Reverse</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {conversions.length === 0 && <p className="text-gray-400 text-center py-8 text-sm">No conversions found.</p>}
            </div>
            <Paginator section="conversions" />
          </CardContent>
        </Card>
      )}

      {/* Payouts Tab */}
      {!loading && tab === 'payouts' && (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Partner</th>
                  <th className="text-right py-3 px-2 text-xs text-gray-400 font-medium">Amount</th>
                  <th className="text-center py-3 px-2 text-xs text-gray-400 font-medium">Status</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Transfer ID</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Created</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Processed</th>
                </tr></thead>
                <tbody>
                  {payouts.map((p, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-3 px-2 font-medium text-[#0f1f33]">{p.partner_name}</td>
                      <td className="py-3 px-2 text-right font-semibold text-emerald-600">{fmt(p.amount)}</td>
                      <td className="py-3 px-2 text-center">{statusBadge(p.status)}</td>
                      <td className="py-3 px-2 font-mono text-xs text-gray-400">{p.stripe_transfer_id || '—'}</td>
                      <td className="py-3 px-2 text-xs text-gray-400">{p.created_at?.slice(0, 10)}</td>
                      <td className="py-3 px-2 text-xs text-gray-400">{p.processed_at?.slice(0, 10) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payouts.length === 0 && <p className="text-gray-400 text-center py-8 text-sm">No payouts found.</p>}
            </div>
            <Paginator section="payouts" />
          </CardContent>
        </Card>
      )}

      {/* Fraud Flags Tab */}
      {!loading && tab === 'fraud' && (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Reason</th>
                  <th className="text-center py-3 px-2 text-xs text-gray-400 font-medium">Severity</th>
                  <th className="text-center py-3 px-2 text-xs text-gray-400 font-medium">Status</th>
                  <th className="text-left py-3 px-2 text-xs text-gray-400 font-medium">Created</th>
                </tr></thead>
                <tbody>
                  {fraudFlags.map((f, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-3 px-2 text-[#0f1f33]">{f.reason}</td>
                      <td className="py-3 px-2 text-center">
                        <Badge className={`text-xs capitalize ${f.severity === 'high' ? 'bg-red-50 text-red-700' : f.severity === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-600'}`}>{f.severity}</Badge>
                      </td>
                      <td className="py-3 px-2 text-center">{statusBadge(f.status)}</td>
                      <td className="py-3 px-2 text-xs text-gray-400">{f.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fraudFlags.length === 0 && <p className="text-gray-400 text-center py-8 text-sm">No fraud flags found.</p>}
            </div>
            <Paginator section="fraud" />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
